import { useCallback, useEffect, useState } from 'react';
import { supabase } from '@/lib/supabase';
import type { PrinterRole, PrinterSettings } from '@/services/print/printTypes';

export const PRINTER_ROLES: PrinterRole[] = ['receipt', 'report', 'label'];

export const ROLE_META: Record<PrinterRole, { title: string; icon: string }> = {
  receipt: { title: 'Impresora de facturas', icon: 'ri-receipt-line' },
  report: { title: 'Impresora de reportes', icon: 'ri-file-chart-line' },
  label: { title: 'Impresora de etiquetas', icon: 'ri-price-tag-3-line' },
};

function defaultSettings(branchId: string, role: PrinterRole): PrinterSettings {
  return {
    branch_id: branchId,
    role,
    printer_name: null,
    printer_id: null,
    printer_type: role === 'receipt' ? 'thermal' : 'standard',
    paper_width: role === 'receipt' ? '80mm' : 'A4',
    copies: 1,
    auto_cut: role === 'receipt',
    open_drawer: false,
    auto_print: role === 'receipt',
    is_default: role === 'receipt',
    is_active: true,
  };
}

export function usePrinterSettings(branchId?: string | null) {
  const [settings, setSettings] = useState<Record<PrinterRole, PrinterSettings> | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!branchId) {
      setSettings(null);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const { data, error: selectError } = await supabase
        .from('printer_settings')
        .select('*')
        .eq('branch_id', branchId);

      if (selectError) throw selectError;

      const rows = (data ?? []) as PrinterSettings[];
      const map = {} as Record<PrinterRole, PrinterSettings>;
      for (const role of PRINTER_ROLES) {
        const row = rows.find((r) => r.role === role);
        map[role] = row ?? defaultSettings(branchId, role);
      }
      setSettings(map);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'No se pudo cargar la configuración de impresoras');
    } finally {
      setLoading(false);
    }
  }, [branchId]);

  useEffect(() => {
    load();
  }, [load]);

  const update = useCallback((role: PrinterRole, patch: Partial<PrinterSettings>) => {
    setSettings((prev) => (prev ? { ...prev, [role]: { ...prev[role], ...patch } } : prev));
  }, []);

  const save = useCallback(async (): Promise<boolean> => {
    if (!settings || !branchId) {
      setError('No hay configuración para guardar.');
      return false;
    }
    setSaving(true);
    setError(null);
    try {
      const rows = PRINTER_ROLES.map((role) => {
        const s = settings[role];
        const { id, created_at, updated_at, ...rest } = s;
        return { ...rest, branch_id: branchId, updated_at: new Date().toISOString() };
      });

      const { error: upsertError } = await supabase
        .from('printer_settings')
        .upsert(rows, { onConflict: 'branch_id,role' });

      if (upsertError) throw upsertError;
      return true;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'No se pudo guardar la configuración.');
      return false;
    } finally {
      setSaving(false);
    }
  }, [settings, branchId]);

  return { settings, loading, saving, error, update, save, reload: load };
}